export type MarketplacePlatform = "SuperRare" | "Foundation" | "Verse"

export interface MarketplaceWork {
  id: string
  title: string
  artistName: string
  platform: MarketplacePlatform
  chainId?: 1
  year: string
  medium: string
  contractAddress: string
  tokenId: string
  creatorAddress: string
  mediaUrl: string
  mediaFallbackUrls?: string[]
  mediaType: "image" | "video"
  aspectRatio: number
  externalUrl: string
  sourceLabel: string
  description: string
}

const ipfsImageUrls = (path: string) => ({
  mediaUrl: `https://gateway.pinata.cloud/ipfs/${path}`,
  mediaFallbackUrls: [`https://ipfs.io/ipfs/${path}`],
})

export const MARKETPLACE_WORKS: MarketplaceWork[] = [
  {
    id: "verse-decal-by-the-community-6",
    title: "Decal by the Community #6",
    artistName: "r4v3n",
    platform: "Verse",
    // NOTE: year approximate — Verse did not expose a mint date; confirm.
    year: "2024",
    medium: "Generative artwork",
    contractAddress: "0x0c5369b13fadb3b139bfe0d16671c68eaf1cdfdc",
    tokenId: "6",
    creatorAddress: "0x65De6475258E3736e5C7d502d2C0a45710c9eC37",
    // Higher-res source (2400x2400) resolved from the token's media; the
    // original is 4700x4700 but too heavy to serve directly.
    mediaUrl:
      "https://verse.works/image/w2400/static%2Fuploads%2F0x0c5369b13fadb3b139bfe0d16671c68eaf1cdfdc%2Fceb6bfa8-9085-4f4e-b283-629b304ebf57.png@jpeg",
    mediaType: "image",
    aspectRatio: 1,
    externalUrl: "https://verse.works/items/ethereum/0x0c5369b13fadb3b139bfe0d16671c68eaf1cdfdc/6",
    sourceLabel: "View on Verse",
    description:
      "An interpretation of the Decal by the Community — a symbol of permissionless creativity, from Series Thirty-One.",
  },
  {
    id: "superrare-tower-50307",
    title: "Tower",
    artistName: "r4v3n",
    platform: "SuperRare",
    year: "2024",
    medium: "Film photography + generative textures",
    contractAddress: "0xb932a70A57673d89f4acfFBE830E8ed7f75Fb9e0",
    tokenId: "50307",
    creatorAddress: "0x65De6475258E3736e5C7d502d2C0a45710c9eC37",
    mediaUrl:
      "https://storage.googleapis.com/sr_prod_artworks_bucket/asset%2Ff82e5443896feb4bd3d410639ca37c17d1ba7e8e1c188aea5a31a0a9417dba68.png",
    mediaType: "image",
    aspectRatio: 1739 / 2345,
    externalUrl:
      "https://superrare.com/artwork/eth/0xb932a70a57673d89f4acffbe830e8ed7f75fb9e0/50307",
    sourceLabel: "View on SuperRare",
    description:
      "A Provincetown film photograph set against generative JavaScript textures, made with Leica M5 and TMax 400 film.",
  },
  {
    id: "superrare-sol-48022",
    title: "Sol",
    artistName: "r4v3n",
    platform: "SuperRare",
    year: "2024",
    medium: "Photography + generative textures",
    contractAddress: "0xb932a70A57673d89f4acfFBE830E8ed7f75Fb9e0",
    tokenId: "48022",
    creatorAddress: "0x65De6475258E3736e5C7d502d2C0a45710c9eC37",
    mediaUrl: "https://ipfs.pixura.io/ipfs/QmPvkfREaZDtzMPD79M4DmPeWqqcjjC3gWQXeXaKG4Coqv/superrare-0.png",
    mediaFallbackUrls: ["https://ipfs.io/ipfs/QmPvkfREaZDtzMPD79M4DmPeWqqcjjC3gWQXeXaKG4Coqv/superrare-0.png"],
    mediaType: "image",
    aspectRatio: 3000 / 3750,
    externalUrl:
      "https://superrare.com/artwork/eth/0xb932a70a57673d89f4acffbe830e8ed7f75fb9e0/48022",
    sourceLabel: "View on SuperRare",
    description:
      "A surreal mixed media work combining a San Clemente photograph with JavaScript textures rendered to HTML5 canvas.",
  },
  {
    id: "foundation-r4v3n-bite-me-2",
    title: "BITE ME",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2022",
    medium: "JavaScript digital image",
    contractAddress: "0x6197578995dB9086CF30247809365754B50cFF26",
    tokenId: "2",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmWFT5878Lqg6U3nbq8ZR6a1t3Ad2ut317WiDnKJGDN9yU/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20230119235334/https://foundation.app/@r4v3n/r4v3n/2",
    sourceLabel: "View 1/1",
    description:
      "A 2022 JavaScript digital image from r4v3n's custom 1/1 collection.",
  },
  {
    id: "foundation-r4v3n-the-dance-1",
    title: "The Dance",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2022",
    medium: "JavaScript digital image",
    contractAddress: "0x6197578995dB9086CF30247809365754B50cFF26",
    tokenId: "1",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmYByBXYJid14RT9PVcuXvf92gTYgxbYnDdWG8PTWuG2qn/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20220425232215/https://foundation.app/@r4v3n/r4v3n/1",
    sourceLabel: "View 1/1",
    description:
      "A 2022 JavaScript digital image from the standalone r4v3n 1/1 collection.",
  },
  {
    id: "foundation-r4v3n-lewitts-souls-4",
    title: "LeWitt's Souls",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2022",
    medium: "JavaScript digital image",
    contractAddress: "0x6197578995dB9086CF30247809365754B50cFF26",
    tokenId: "4",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmVbe3HCgfdMPryxokpQpcQZwhKnbUtdB5UfL4SdzHyvXC/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20220124141745/https://foundation.app/@r4v3n/r4v3n/4",
    sourceLabel: "View 1/1",
    description:
      "A generative homage to Sol LeWitt's wall-drawing instructions, sold as a r4v3n 1/1.",
  },
  {
    id: "foundation-r4v3n-off-limits-3",
    title: "OFF LIMITS",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2022",
    medium: "JavaScript digital image",
    contractAddress: "0x6197578995dB9086CF30247809365754B50cFF26",
    tokenId: "3",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmXJkXJxvADVKNezMxK3vPGfUYRD7Gj5EwA1NgsUTrQQhy/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20220219232836/https://foundation.app/@r4v3n/r4v3n/3",
    sourceLabel: "View 1/1",
    description:
      "A 2022 JavaScript digital image from r4v3n's 1/1 collection.",
  },
  {
    id: "foundation-happy-accident-91767",
    title: "Happy Accident",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2021",
    medium: "JavaScript",
    contractAddress: "0x3B3ee1931Dc30C1957379FAc9aba94D1C48a5405",
    tokenId: "91767",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmTDnAttzGHQccZzMCUF1GRTmcjJhAg2Fj57m5MxvEnERZ/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20211105004733/https://foundation.app/@r4v3n/~/91767",
    sourceLabel: "View 1/1",
    description:
      "A one-off accident that emerged while developing the Moments series for Art Blocks.",
  },
  {
    id: "foundation-ghost-randomness-104741",
    title: "Ghost in the Randomness",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2021",
    medium: "JavaScript, 3000x3000 digital image",
    contractAddress: "0x3B3ee1931Dc30C1957379FAc9aba94D1C48a5405",
    tokenId: "104741",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("Qma4i5E23XQSveTZc5MZbfZG1RfqrBUdCwa6avAUrRBcjr/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20211120034610/https://foundation.app/@r4v3n/~/104741",
    sourceLabel: "View 1/1",
    description:
      "A computational apparition from deep inside generative randomness, sold as a 1/1.",
  },
  {
    id: "foundation-portrait-war-119950",
    title: "A Portrait of War",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2021",
    medium: "JavaScript",
    contractAddress: "0x3B3ee1931Dc30C1957379FAc9aba94D1C48a5405",
    tokenId: "119950",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmY7S9v1F1GNz63agXa415vQRsf5SyDWVC1bThpG94pe57/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20211217205035/https://foundation.app/@r4v3n/~/119950",
    sourceLabel: "View 1/1",
    description:
      "A compact generative work from 2021: diplomacy has failed, engage rage.",
  },
  {
    id: "foundation-shields-up-119961",
    title: "Shields Up",
    artistName: "r4v3n",
    platform: "Foundation",
    year: "2021",
    medium: "JavaScript",
    contractAddress: "0x3B3ee1931Dc30C1957379FAc9aba94D1C48a5405",
    tokenId: "119961",
    creatorAddress: "0x4d9216E3C0922e1d429DDd7fB0FF39282CCD765D",
    ...ipfsImageUrls("QmZNjPQNUAPhcuDM97GRnhdp82YFngrMMdxibpkc2ZFdJE/nft.png"),
    mediaType: "image",
    aspectRatio: 1,
    externalUrl:
      "https://web.archive.org/web/20211218113932/https://foundation.app/@r4v3n/~/119961",
    sourceLabel: "View 1/1",
    description:
      "A 2021 JavaScript work where uncertain forms appear out of the ether.",
  },
]

export const getMarketplaceWorkDetailHref = (work: MarketplaceWork) =>
  `/token/${work.contractAddress}/${work.tokenId}`

export const getMarketplaceWorkMediaUrls = (work: MarketplaceWork) =>
  [work.mediaUrl, ...(work.mediaFallbackUrls ?? [])].filter(
    (url, index, urls) => urls.indexOf(url) === index
  )

export const findMarketplaceWorkByToken = (contractAddress: string, tokenId: string) =>
  MARKETPLACE_WORKS.find((work) =>
    work.contractAddress.toLowerCase() === contractAddress.toLowerCase() &&
    work.tokenId === tokenId
  )
